// src/repositories/index.ts
//
// Punto de entrada del Data Access Layer.
// Importa siempre desde aquí — nunca desde los archivos individuales.
//
// Cloud-First: factory usa implementaciones Supabase.
// Dexie repos se conservan en el repo como referencia.

import type {
  ICompanyRepository,
  ITransactionRepository,
  IJournalEntryRepository,
  ISunatRecordRepository,
  IEntityRepository,
  IInvoicePaymentRepository,
  IAgentProposalRepository,
} from './types';
import { supabase } from '../services/supabaseClient';
import { SupabaseCompanyRepository }        from './supabase/SupabaseCompanyRepository';
import { SupabaseTransactionRepository }    from './supabase/SupabaseTransactionRepository';
import { SupabaseJournalEntryRepository }   from './supabase/SupabaseJournalEntryRepository';
import { SupabaseSunatRecordRepository }    from './supabase/SupabaseSunatRecordRepository';
import { SupabaseEntityRepository }         from './supabase/SupabaseEntityRepository';
import { SupabaseInvoicePaymentRepository } from './supabase/SupabaseInvoicePaymentRepository';
import { SupabaseAgentProposalRepository } from './supabase/SupabaseAgentProposalRepository';

export type {
  ICompanyRepository,
  ITransactionRepository,
  IJournalEntryRepository,
  ISunatRecordRepository,
  IEntityRepository,
  IInvoicePaymentRepository,
  IAgentProposalRepository,
  OverdueInvoiceRow,
  DBCompany,
  DBTransaction,
  DBJournalEntry,
  DBSunatRecord,
  DBEntity,
} from './types';

export { PropuestaDuplicadaError } from './types';

export interface Repositories {
  companies:        ICompanyRepository;
  transactions:     ITransactionRepository;
  journalEntries:   IJournalEntryRepository;
  sunatRecords:     ISunatRecordRepository;
  entities:         IEntityRepository;
  invoicePayments:  IInvoicePaymentRepository;
}

/**
 * Creates Supabase-backed repository instances.
 * The Supabase client is injected once and shared across all repos.
 */
export function createRepositories(): Repositories {
  return {
    companies:       new SupabaseCompanyRepository(supabase),
    transactions:    new SupabaseTransactionRepository(supabase),
    journalEntries:  new SupabaseJournalEntryRepository(supabase),
    sunatRecords:    new SupabaseSunatRecordRepository(supabase),
    entities:        new SupabaseEntityRepository(supabase),
    invoicePayments: new SupabaseInvoicePaymentRepository(supabase),
  };
}

/**
 * Repositorio de propuestas de agentes (Fase 2). Va aparte de `Repositories` a propósito: así los
 * agentes y tests existentes que arman un `Repositories` completo no cambian.
 */
export function createAgentProposalRepository(): IAgentProposalRepository {
  return new SupabaseAgentProposalRepository(supabase);
}
